/*
|--------------------------------------------------------------------------
| Type Guards
|--------------------------------------------------------------------------
|
| Utility functions that help determine the type of unknown values
|
*/

import { joi } from '@nhtio/lucid-resourceful/joi'
import { stripUndefinedValuesFromObject } from './utils'
import {
  isException,
  isJoiValidationError,
  isVineValidationError,
  isNotImplementedError,
} from './router_macro/utils/errors'
import {
  isArray,
  isBigInt,
  isBigIntTypedArray,
  isError,
  isInstanceOf,
  isLucidBinaryValue,
  isLuxonDateTime,
  isLuxonDuration,
  isLuxonInterval,
  isLuxonSystemZone,
  isMap,
  isNegativeZero,
  isNumber,
  isObject,
  isPhoneObject,
  isPrimitive,
  isSet,
  isTypedArray,
  isUniterableObject,
} from '@nhtio/encoder/type_guards'
import {
  ResourcefulStringTypeSchema,
  ResourcefulDateTypeSchema,
  ResourcefulDateTimeTypeSchema,
  ResourcefulBinaryTypeSchema,
  ResourcefulNumberTypeSchema,
  ResourcefulIntegerTypeSchema,
  ResourcefulBigintTypeSchema,
  ResourcefulUnsignedIntegerTypeSchema,
  ResourcefulBooleanTypeSchema,
  ResourcefulObjectTypeSchema,
  ResourcefulArrayTypeSchema,
} from './data_type_schemas'
import type { OpenAPIV3 } from 'openapi-types'
import type { ResourcefulModel } from './mixin'
import type { ObjectSchema, AnySchema } from '@nhtio/lucid-resourceful/joi'
import type {
  ResourcefulStringType,
  ResourcefulDateType,
  ResourcefulDateTimeType,
  ResourcefulBinaryType,
  ResourcefulNumberType,
  ResourcefulIntegerType,
  ResourcefulBigintType,
  ResourcefulUnsignedIntegerType,
  ResourcefulBooleanType,
  ResourcefulObjectType,
  ResourcefulArrayType,
} from '@nhtio/lucid-resourceful/definitions'

export {
  isArray,
  isBigInt,
  isBigIntTypedArray,
  isError,
  isInstanceOf,
  isLucidBinaryValue,
  isLuxonDateTime,
  isLuxonDuration,
  isLuxonInterval,
  isLuxonSystemZone,
  isMap,
  isNegativeZero,
  isNumber,
  isObject,
  isPhoneObject,
  isPrimitive,
  isSet,
  isTypedArray,
  isUniterableObject,
  isException,
  isJoiValidationError,
  isVineValidationError,
  isNotImplementedError,
}

const isResourcefulModelCache = new Map<unknown, boolean>()

/**
 * Type guard to check if a value is a ResourcefulModel
 * @param value - The value to check
 * @returns True if the value is a ResourcefulModel, false otherwise
 */
export const isResourcefulModel = (value: unknown): value is ResourcefulModel => {
  const cached = isResourcefulModelCache.get(value)
  if ('undefined' !== typeof cached) {
    return cached
  }
  if (!isObject(value) && !(typeof value === 'function')) return false

  // For class constructors (functions), check the properties directly on the constructor
  if (typeof value === 'function') {
    const returnable =
      '$resourcefulName' in value &&
      'string' === typeof value.$resourcefulName &&
      '$resourcefulColumns' in value &&
      value.$resourcefulColumns instanceof Map &&
      '$resourcefulRelationships' in value &&
      value.$resourcefulRelationships instanceof Map &&
      '$resourcefulComputedAccessors' in value &&
      value.$resourcefulComputedAccessors instanceof Map
    isResourcefulModelCache.set(value, returnable)
    return returnable
  }

  // For instances, check the constructor of the instance
  /* istanbul ignore if -- @preserve */
  if ('constructor' in value) {
    // @ts-ignore
    const constructor = value.constructor
    /* istanbul ignore if -- @preserve */
    if (!constructor || typeof constructor !== 'function') {
      isResourcefulModelCache.set(value, false)
      return false
    }

    return isResourcefulModel(constructor) // Recursively check the constructor
  }

  /* istanbul ignore next -- @preserve */
  const proto = Object.getPrototypeOf(value)
  return isResourcefulModel(proto)
}

const BaseDataTypeSchemaFactory = (name: string, type: string) => {
  return joi
    .object({
      type: joi.string().allow(type).required(),
      name: joi.string().allow(name).optional(),
      schema: joi
        .object({
          _cache: joi.required(),
          _flags: joi.object().unknown(true).required(),
          _ids: joi
            .object({
              _byId: joi.object().instance(Map).required(),
              _byKey: joi.object().instance(Map).required(),
              _schemaChain: joi.boolean().required(),
            })
            .unknown(true)
            .required(),
          _invalids: joi.required(),
          _preferences: joi.required(),
          _refs: joi.object().unknown(true).required(),
          _rules: joi.array().items(joi.any()).required(),
          _singleRules: joi.object().instance(Map).required(),
          _valids: joi.required(),
          $: joi.required(),
          $_root: joi.required(),
          $_super: joi.required(),
          $_temp: joi.required(),
          $_terms: joi.required(),
          ruleset: joi.func().required(),
          type: joi.string().required(),
        })
        .unknown(true)
        .optional(),
    })
    .unknown(true)
}

const getDataTypeSchemaResults = (
  value: unknown,
  name: string,
  type: string,
  schema: ObjectSchema
) => {
  if (!isObject(value)) return false
  if (value.type !== type) return false
  const base = BaseDataTypeSchemaFactory(name, type)
  const { error: errorFromSchema } = schema.validate(
    stripUndefinedValuesFromObject({
      ...value,
      type: undefined,
      name: undefined,
      schema: undefined,
    })
  )
  const { error: errorFromBase } = base.validate({
    type: value.type,
    name: value.name,
    schema: value.schema,
  })
  return 'undefined' === typeof errorFromSchema && 'undefined' === typeof errorFromBase
}

export const isResourcefulStringType = (value: unknown): value is ResourcefulStringType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulStringType',
    'string',
    ResourcefulStringTypeSchema()
  )
}

/**
 * Type guard to check if a value is a ResourcefulDateType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulDateType, false otherwise
 */
export const isResourcefulDateType = (value: unknown): value is ResourcefulDateType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulDateType',
    'string',
    ResourcefulDateTypeSchema().append({
      format: joi.string().valid('date').required(),
    })
  )
}

/**
 * Type guard to check if a value is a ResourcefulDateTimeType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulDateTimeType, false otherwise
 */
export const isResourcefulDateTimeType = (value: unknown): value is ResourcefulDateTimeType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulDateTimeType',
    'string',
    ResourcefulDateTimeTypeSchema().append({
      format: joi.string().valid('date-time').required(),
    })
  )
}

/**
 * Type guard to check if a value is a ResourcefulBinaryType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulBinaryType, false otherwise
 */
export const isResourcefulBinaryType = (value: unknown): value is ResourcefulBinaryType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulBinaryType',
    'string',
    ResourcefulBinaryTypeSchema().append({
      format: joi.string().valid('binary').required(),
    })
  )
}

/**
 * Type guard to check if a value is a ResourcefulNumberType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulNumberType, false otherwise
 */
export const isResourcefulNumberType = (value: unknown): value is ResourcefulNumberType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulNumberType',
    'number',
    ResourcefulNumberTypeSchema()
  )
}

/**
 * Type guard to check if a value is a ResourcefulIntegerType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulIntegerType, false otherwise
 */
export const isResourcefulIntegerType = (value: unknown): value is ResourcefulIntegerType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulIntegerType',
    'integer',
    ResourcefulIntegerTypeSchema().append({
      format: joi.string().valid('int32').required(),
    })
  )
}

/**
 * Type guard to check if a value is a ResourcefulBigintType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulBigintType, false otherwise
 */
export const isResourcefulBigintType = (value: unknown): value is ResourcefulBigintType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulBigintType',
    'integer',
    ResourcefulBigintTypeSchema().append({
      format: joi.string().valid('int64').required(),
    })
  )
}

/**
 * Type guard to check if a value is a ResourcefulUnsignedIntegerType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulUnsignedIntegerType, false otherwise
 */
export const isResourcefulUnsignedIntegerType = (
  value: unknown
): value is ResourcefulUnsignedIntegerType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulUnsignedIntegerType',
    'integer',
    ResourcefulUnsignedIntegerTypeSchema()
  )
}

/**
 * Type guard to check if a value is a ResourcefulBooleanType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulBooleanType, false otherwise
 */
export const isResourcefulBooleanType = (value: unknown): value is ResourcefulBooleanType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulBooleanType',
    'boolean',
    ResourcefulBooleanTypeSchema()
  )
}

/**
 * Type guard to check if a value is a ResourcefulObjectType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulObjectType, false otherwise
 */
export const isResourcefulObjectType = (value: unknown): value is ResourcefulObjectType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulObjectType',
    'object',
    ResourcefulObjectTypeSchema()
  )
}

/**
 * Type guard to check if a value is a ResourcefulArrayType
 * @param value - The value to check
 * @returns True if the value is a ResourcefulArrayType, false otherwise
 */
export const isResourcefulArrayType = (value: unknown): value is ResourcefulArrayType => {
  return getDataTypeSchemaResults(
    value,
    'ResourcefulArrayType',
    'array',
    ResourcefulArrayTypeSchema()
  )
}

const matchesSchema = (value: unknown, schema: AnySchema): boolean => {
  const { error } = schema.validate(value, {
    abortEarly: true,
  })
  return !error
}

const stringSchema = joi.string().allow('').required()
const booleanSchema = joi.boolean().required()
const bigintSchema = joi
  .any()
  .custom((value, helpers) => {
    if (typeof value === 'bigint') {
      return value
    }
    return helpers.error('any.invalid')
  })
  .required()
const nullSchema = joi.any().valid(null).required()
const undefinedSchema = joi.any().custom((value, helpers) => {
  /* v8 ignore next 3 */
  if (typeof value === 'undefined') {
    return value
  }
  return helpers.error('any.invalid')
})

const int8ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Int8Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const uint8ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Uint8Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const uint8ClampedArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Uint8ClampedArray) {
    return value
  }
  return helpers.error('any.invalid')
})
const int16ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Int16Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const uint16ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Uint16Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const int32ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Int32Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const uint32ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Uint32Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const float32ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Float32Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const float64ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof Float64Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const bigInt64ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof BigInt64Array) {
    return value
  }
  return helpers.error('any.invalid')
})
const bigUint64ArraySchema = joi.any().custom((value, helpers) => {
  if (value instanceof BigUint64Array) {
    return value
  }
  return helpers.error('any.invalid')
})

const dateSchema = joi.any().custom((value, helpers) => {
  if (value instanceof Date) {
    return value
  }
  return helpers.error('any.invalid')
})
const regExpSchema = joi.any().custom((value, helpers) => {
  if (value instanceof RegExp) {
    return value
  }
  return helpers.error('any.invalid')
})
const arrayBufferSchema = joi.any().custom((value, helpers) => {
  if (value instanceof ArrayBuffer) {
    return value
  }
  return helpers.error('any.invalid')
})
const dataViewSchema = joi.any().custom((value, helpers) => {
  if (value instanceof DataView) {
    return value
  }
  return helpers.error('any.invalid')
})

export const isString = (value: unknown): value is string => {
  return matchesSchema(value, stringSchema)
}

export const isBoolean = (value: unknown): value is boolean => {
  return matchesSchema(value, booleanSchema)
}

export const isBigint = (value: unknown): value is bigint => {
  return matchesSchema(value, bigintSchema)
}

export const isNull = (value: unknown): value is null => {
  return matchesSchema(value, nullSchema)
}

export const isUndefined = (value: unknown): value is undefined => {
  return matchesSchema(value, undefinedSchema)
}

export const isInt8Array = (value: unknown): value is Int8Array => {
  return matchesSchema(value, int8ArraySchema)
}

export const isUint8Array = (value: unknown): value is Uint8Array => {
  return matchesSchema(value, uint8ArraySchema)
}

export const isUint8ClampedArray = (value: unknown): value is Uint8ClampedArray => {
  return matchesSchema(value, uint8ClampedArraySchema)
}

export const isInt16Array = (value: unknown): value is Int16Array => {
  return matchesSchema(value, int16ArraySchema)
}

export const isUint16Array = (value: unknown): value is Uint16Array => {
  return matchesSchema(value, uint16ArraySchema)
}

export const isInt32Array = (value: unknown): value is Int32Array => {
  return matchesSchema(value, int32ArraySchema)
}

export const isUint32Array = (value: unknown): value is Uint32Array => {
  return matchesSchema(value, uint32ArraySchema)
}

export const isFloat32Array = (value: unknown): value is Float32Array => {
  return matchesSchema(value, float32ArraySchema)
}

export const isFloat64Array = (value: unknown): value is Float64Array => {
  return matchesSchema(value, float64ArraySchema)
}

export const isBigInt64Array = (value: unknown): value is BigInt64Array => {
  return matchesSchema(value, bigInt64ArraySchema)
}

export const isBigUint64Array = (value: unknown): value is BigUint64Array => {
  return matchesSchema(value, bigUint64ArraySchema)
}

export const isDate = (value: unknown): value is Date => {
  return matchesSchema(value, dateSchema)
}

export const isRegExp = (value: unknown): value is RegExp => {
  return matchesSchema(value, regExpSchema)
}

export const isArrayBuffer = (value: unknown): value is ArrayBuffer => {
  return matchesSchema(value, arrayBufferSchema)
}

export const isDataView = (value: unknown): value is DataView => {
  return matchesSchema(value, dataViewSchema)
}

export const isNonArraySchemaObject = (
  value?: OpenAPIV3.SchemaObject | undefined
): value is OpenAPIV3.NonArraySchemaObject => {
  return isObject(value) && value.type !== 'array'
}

export { isStringContent } from './utils'
